import Image from "next/image";
import Link from "next/link";
import { PenLine } from "lucide-react";
import { CardsList } from "@/lib/interfaces/cards";
import FavoriteButton from "./FavoriteButton";

interface CategoryCardProps {
  card: CardsList;
  index: number;
  isFavorite?: boolean;
}

/**
 * One design in the category grid. A white frame + cream image panel keeps
 * light/pastel designs readable on any background, and the card name is real
 * text (not just alt text) so crawlers and AI agents can read every design.
 */
export default function CategoryCard({ card, index, isFavorite = false }: CategoryCardProps) {
  return (
    <li className={`group relative${index >= 4 ? " cv-card" : ""}`}>
      <FavoriteButton cardId={card.id} initialIsFavorite={isFavorite} className="!left-4 !top-4" />

      <Link
        href={`/create/${card.slug}`}
        prefetch={false}
        className="block rounded-2xl border border-paper-rule bg-white p-2 shadow-[0_1px_2px_rgba(6,35,63,0.06)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_18px_36px_-18px_rgba(6,35,63,0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink"
      >
        <div className="relative aspect-[4/5] overflow-hidden rounded-xl bg-paper ring-1 ring-black/5">
          <Image
            src={card.card_image}
            alt={card.name}
            fill
            sizes="(min-width: 1280px) 230px, (min-width: 1024px) 22vw, (min-width: 768px) 30vw, 46vw"
            className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            // Only the first row (the mobile LCP) is preloaded at high priority;
            // everything else is lazy so it doesn't compete for bandwidth.
            {...(index < 2 ? { priority: true, fetchPriority: "high" as const } : { loading: "lazy" as const })}
          />

          {card.is_new ? (
            <span className="absolute right-2 top-2 rounded-full bg-heart-600 px-2 py-0.5 text-[11px] font-semibold text-white shadow-sm">New</span>
          ) : card.is_staff_pick ? (
            <span className="absolute right-2 top-2 rounded-full bg-marigold px-2 py-0.5 text-[11px] font-semibold text-ink shadow-sm">Staff pick</span>
          ) : null}

          {/* Hover / focus call to action */}
          <span className="pointer-events-none absolute inset-x-2 bottom-2 flex translate-y-2 items-center justify-center gap-1.5 rounded-full bg-ink/95 py-2 text-xs font-semibold text-white opacity-0 shadow-lg transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:translate-y-0 group-focus-within:opacity-100">
            <PenLine className="h-3.5 w-3.5" aria-hidden="true" />
            Personalise &amp; send
          </span>
        </div>

        <p className="line-clamp-1 px-1 pb-0.5 pt-2.5 text-[13px] font-medium leading-snug text-ink" title={card.name}>
          {card.name}
        </p>
      </Link>
    </li>
  );
}
